import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import moment from 'moment';

import { IconDownload } from 'svgIcons/MotionPortalIcons';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import { APP, SMART_SENSOR_DCU, SMART_SENSOR, DRIVE_DCU, EventTypesIconWithName } from '../constants';

const GatewayEventRow = ({ event, actions, eventSource }) => {
    const fileNameRef = useRef(null);
    const organizationRef = useRef(null);
    const localTimeRef = useRef(null);
    const utcTimeRef = useRef(null);
    const userEmailRef = useRef(null);

    const [isTruncated, setIsTruncated] = useState({
        fileName: false,
        organization: false,
        localTime: false,
        utcTime: false,
        userEmail: false
    });

    useEffect(() => {
        const checkTruncation = () => {
            const checkElement = (ref) => {
                if (!ref.current) { return false; }
                return ref.current.scrollWidth > ref.current.clientWidth;
            };

            setIsTruncated({
                fileName: checkElement(fileNameRef),
                organization: checkElement(organizationRef),
                localTime: checkElement(localTimeRef),
                utcTime: checkElement(utcTimeRef),
                userEmail: checkElement(userEmailRef)
            });
        };

        // Check multiple times to ensure DOM is ready
        const timer1 = setTimeout(checkTruncation, 100);
        const timer2 = setTimeout(checkTruncation, 300);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, [event?.fileName, event?.messageText, event?.organization, event?.timestamp, event?.userEmailAddress]);

    const handleAttachmentClick = useCallback((e) => {
        if (event?.hasAttachments && actions?.fetchGatewayEventsAttachment) {
            e.preventDefault();
            e.stopPropagation();
            actions.fetchGatewayEventsAttachment({
                globalEventId: event?.eventId,
                eventSource: event?.eventSource
            });
        }
    }, [event, actions]);

    // Tab type constants
    const isAppTab = eventSource === APP;
    const isGatewayTab = eventSource === SMART_SENSOR_DCU;
    const isDriveDCUTab = eventSource === DRIVE_DCU;
    const isSmartSensorTab = eventSource === SMART_SENSOR;
    const isSimpleTab = isDriveDCUTab || isSmartSensorTab;

    // Icon + tooltip name cell used by Drive DCU and SmartSensor tabs
    const eventIcon = isSimpleTab
        ? EventTypesIconWithName.find(et => et.typeID === event?.severityCode)?.icon
        : null;

    const showTooltip = isTruncated.fileName;
    const eventNameContent =
        <div ref={fileNameRef} className='event-name-text'>
            {event?.messageText}
        </div>
        ;

    const formatLocalDateTime = (timestamp) => {
        if (!timestamp) { return '-'; }
        const m = moment.parseZone(timestamp).local();
        if (!m?.isValid()) { return '-'; }
        return formatDateTime(m.toDate());
    };

    const formatUtcTimestampDisplayFromFormatter = (timestamp) => {
        if (!timestamp) { return '-'; }
        const m = formatDateTime(timestamp);
        return m || timestamp;
    };

    // Attachment cell (shared)
    const attachmentCell = event?.hasAttachments ?
        <button
            type='button'
            className='gateway-event-cell attachment-cell clickable'
            onClick={handleAttachmentClick}
            aria-label={`Download attachment for event: ${event?.fileName || event?.messageText}`}
            id='download-event-button-id'
        >
            <div className='download-event-attachment' id='download-event-attachment-id'>
                <IconDownload width='14px' height='14px' />
            </div>
        </button>

        : <div id={`attachment-cell-${event?.eventId}`} className='gateway-event-cell attachment-cell' />;

    if (isAppTab) {
        return (
            <div className='gateway-event-row gateway-event-row--app' id='gateway-event-row-id'>
                <div id={`event-name-${event?.eventId}`} className='gateway-event-cell fileName-cell'>
                    {isTruncated.fileName && event?.fileName ?
                        <Tippy content={event?.fileName} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={fileNameRef}>{event?.fileName}</span>
                        </Tippy>
                        :
                        <span ref={fileNameRef}>{event?.fileName}</span>
                    }
                </div>
                <div id={`organization-${event?.eventId}`} className='gateway-event-cell organization-cell'>
                    {isTruncated.organization && event?.organization && event?.organization !== '-' ?
                        <Tippy content={event?.organization} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={organizationRef}>{event?.organization}</span>
                        </Tippy>
                        :
                        <span ref={organizationRef}>{event?.organization || '-'}</span>
                    }
                </div>
                <div id={`timestamp-${event?.eventId}`} className='gateway-event-cell timestamp-cell'>
                    {isTruncated.localTime ?
                        <Tippy content={formatLocalDateTime(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                        </Tippy>
                        :
                        <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                    }
                </div>
                <div id={`utc-timestamp-${event?.eventId}`} className='gateway-event-cell utc-timestamp-cell'>
                    {isTruncated.utcTime ?
                        <Tippy content={formatUtcTimestampDisplayFromFormatter(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                        </Tippy>
                        :
                        <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                    }
                </div>
                <div id={`user-${event?.eventId}`} className='gateway-event-cell user-cell'>
                    {isTruncated.userEmail && event?.userEmailAddress && event?.userEmailAddress !== '-' ?
                        <Tippy content={event?.userEmailAddress} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={userEmailRef}>{event?.userEmailAddress}</span>
                        </Tippy>
                        :
                        <span ref={userEmailRef}>{event?.userEmailAddress || '-'}</span>
                    }
                </div>
                <div id={`app-version-${event?.eventId}`} className='gateway-event-cell app-version-cell'>
                    <span>{event?.appVersion || '-'}</span>
                </div>
                <div id={`os-version-${event?.eventId}`} className='gateway-event-cell os-version-cell'>
                    <span>{event?.osVersion || '-'}</span>
                </div>
                {attachmentCell}
            </div>
        );
    }

    if (isGatewayTab) {
        return (
            <div className='gateway-event-row gateway-event-row--gateway' id='gateway-event-row-id'>
                <div id={`event-name-${event?.eventId}`} className='gateway-event-cell fileName-cell'>
                    {isTruncated.fileName && event?.fileName ?
                        <Tippy content={event?.fileName} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={fileNameRef}>{event?.fileName}</span>
                        </Tippy>
                        :
                        <span ref={fileNameRef}>{event?.fileName}</span>
                    }
                </div>
                <div id={`type-${event?.eventId}`} className='gateway-event-cell type-cell'>
                    <span>{event?.type || '-'}</span>
                </div>
                <div id={`serial-number-${event?.eventId}`} className='gateway-event-cell serial-number-cell'>
                    {event?.isDeleted ? '' : event?.dcuSerialNumber || '-'}
                </div>
                <div id={`level-${event?.eventId}`} className='gateway-event-cell level-cell'>
                    <span>{event?.level || event?.severityCode || '-'}</span>
                </div>
                <div id={`organization-${event?.eventId}`} className='gateway-event-cell organization-cell'>
                    {isTruncated.organization && event?.organization && event?.organization !== '-' ?
                        <Tippy content={event?.organization} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={organizationRef}>{event?.organization}</span>
                        </Tippy>
                        :
                        <span ref={organizationRef}>{event?.organization || '-'}</span>
                    }
                </div>
                <div id={`timestamp-${event?.eventId}`} className='gateway-event-cell timestamp-cell'>
                    {isTruncated.localTime ?
                        <Tippy content={formatLocalDateTime(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                        </Tippy>
                        :
                        <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                    }
                </div>
                <div id={`utc-timestamp-${event?.eventId}`} className='gateway-event-cell utc-timestamp-cell'>
                    {isTruncated.utcTime ?
                        <Tippy content={formatUtcTimestampDisplayFromFormatter(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                            <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                        </Tippy>
                        :
                        <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                    }
                </div>
                {attachmentCell}
            </div>
        );
    }

    // Drive DCU / SmartSensor tabs
    return (
        <div className='gateway-event-row gateway-event-row--simple' id='gateway-event-row-id'>
            <div id={`event-name-${event?.eventId}`} className='gateway-event-cell event-name-cell'>
                <div className='event-type-icon' id='event-type-icon-id'>{eventIcon}</div>
                {showTooltip ?
                    <Tippy
                        content={event?.messageText}
                        interactive={true}
                        interactiveBorder={10}
                        animation='scale'
                        theme='light-border'
                        trigger='mouseenter'
                        maxWidth='400px'
                        boundary='viewport'
                        appendTo={document.body}
                    >
                        {eventNameContent}
                    </Tippy>
                    :
                    eventNameContent
                }
            </div>
            <div id={`serial-number-${event?.eventId}`} className='gateway-event-cell serial-number-cell'>
                {event?.isDeleted ? '' : event?.dcuSerialNumber || '-'}
            </div>
            <div id={`timestamp-${event?.eventId}`} className='gateway-event-cell timestamp-cell'>
                {isTruncated.localTime ?
                    <Tippy content={formatLocalDateTime(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                        <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                    </Tippy>
                    :
                    <span ref={localTimeRef}>{formatLocalDateTime(event?.timestamp)}</span>
                }
            </div>
            <div id={`utc-timestamp-${event?.eventId}`} className='gateway-event-cell utc-timestamp-cell'>
                {isTruncated.utcTime ?
                    <Tippy content={formatUtcTimestampDisplayFromFormatter(event?.timestamp)} theme='light-border' trigger='mouseenter' maxWidth='400px'>
                        <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                    </Tippy>
                    :
                    <span ref={utcTimeRef}>{formatUtcTimestampDisplayFromFormatter(event?.timestamp)}</span>
                }
            </div>
            {attachmentCell}
        </div>
    );
};

GatewayEventRow.propTypes = {
    eventSource: PropTypes.string,
    event: PropTypes.shape({
        eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        fileName: PropTypes.string,
        severityCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dataCollectionUnitId: PropTypes.number,
        eventSource: PropTypes.string,
        timestamp: PropTypes.string,
        hasAttachments: PropTypes.bool,
        isDeleted: PropTypes.bool,
        organization: PropTypes.string,
        eventTypeName: PropTypes.string,
        appVersion: PropTypes.string,
        osVersion: PropTypes.string,
        userEmailAddress: PropTypes.string,
        messageText: PropTypes.string,
        type: PropTypes.string,
        level: PropTypes.string
    }),
    actions: PropTypes.shape({
        fetchGatewayEventsAttachment: PropTypes.func
    })
};

GatewayEventRow.defaultProps = {
    actions: {}
};

export default React.memo(GatewayEventRow);
